import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STAGE_LABELS, STAGES_ORDER, currency, CHECKLIST_ITEMS, timeAgo, hasRole } from "@/lib/constants";
import SLABadge from "@/components/SLABadge";
import ContractActionsBar from "@/components/ContractActionsBar";
import ReworkBanner from "@/components/ReworkBanner";
import MentionInput from "@/components/MentionInput";
import PeopleStrip from "@/components/PeopleStrip";
import LinkedContractsPanel from "@/components/LinkedContractsPanel";
import LinkParentModal from "@/components/LinkParentModal";
import LinkChildrenModal from "@/components/LinkChildrenModal";
import FamilyTimeline from "@/components/FamilyTimeline";
import MessagePersonModal from "@/components/MessagePersonModal";
import AmendmentDiff from "@/components/AmendmentDiff";
import ApprovalsPanel from "@/components/ApprovalsPanel";
import LifecycleChain from "@/components/LifecycleChain";
import RenewalConfirmBanner from "@/components/RenewalConfirmBanner";
import SpendPanel from "@/components/SpendPanel";
import { ResultModal as EvaluationResultModal } from "@/components/ContractEvaluatorLauncher";
import { toast } from "sonner";
import {
  Sparkles, FileText, AlertTriangle, MessageSquare, ClipboardList, History,
  Upload, ChevronRight, CheckCircle2, XCircle, Flag, Plus, Send, RefreshCw, FileDown, ExternalLink, Activity, Calendar, Check, DollarSign
} from "lucide-react";

const RED_FLAG_TYPES = [
  { key: "arbitration", label: "Arbitration Clause" },
  { key: "auto_renewal", label: "Auto-Renewal" },
  { key: "cpi_escalator", label: "CPI / Price Escalator" },
  { key: "termination_restriction", label: "Termination Restrictions" },
  { key: "indemnification", label: "Indemnification / Liability Caps" },
  { key: "insurance_gap", label: "Insurance Requirement Gaps" },
  { key: "phi_baa", label: "Data / PHI / BAA Concerns" },
  { key: "non_standard_payment", label: "Non-Standard Payment Terms" },
  { key: "custom", label: "Other (Custom)" },
];

function renderMd(md) {
  if (!md) return null;
  // P1-4: use react-markdown + rehype-sanitize so AI/user content can never
  // execute HTML. Custom rehype schema handled by rehypeSanitize defaults
  // (no scripts, iframes, event handlers, or style attrs). Links open safely.
  const linkProps = { target: "_blank", rel: "noopener noreferrer" };
  return (
    <div className="brief-md">
      <ReactMarkdown
        rehypePlugins={[rehypeSanitize]}
        components={{
          a: ({ node, ...props }) => <a {...linkProps} {...props} />,
        }}
      >
        {md}
      </ReactMarkdown>
    </div>
  );
}

function SnoozeAuditPanel({ contract }) {
  const [open, setOpen] = useState(false);
  const items = useMemo(() => {
    const rows = [];
    for (const f of contract?.red_flags || []) {
      const events = f.snooze_events || [];
      const stub_milestones = f.snoozed_milestones || [];
      // Prefer detailed events, fall back to milestone-only stub.
      if (events.length) {
        events.forEach(e => rows.push({
          milestone: e.milestone, by: e.by_name, at: e.at,
          flag_type: f.flag_type,
        }));
      } else if (stub_milestones.length) {
        stub_milestones.forEach(m => rows.push({
          milestone: m, by: "—", at: null, flag_type: f.flag_type,
        }));
      }
    }
    return rows.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  }, [contract]);
  if (items.length === 0) return null;
  return (
    <div className="mt-2" data-testid="snooze-audit-panel">
      <button onClick={() => setOpen(v => !v)}
              className="chip chip-draft cursor-pointer hover:opacity-80"
              data-testid="snooze-audit-toggle">
        <span className="dot" />{items.length} alert{items.length > 1 ? "s" : ""} snoozed
        <span className="ml-1 text-[9px]">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="mt-2 border border-slate-200 rounded-md bg-white p-3 max-w-xl" data-testid="snooze-audit-list">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Snooze History</div>
          <ul className="space-y-1.5">
            {items.map((it, i) => (
              <li key={i} className="text-xs text-slate-700 flex items-center gap-2">
                <span className="chip chip-completed"><span className="dot" />{it.milestone}-day</span>
                <span className="text-slate-500">on the</span>
                <span className="font-semibold">{(it.flag_type || "flag").replace(/_/g, " ")}</span>
                <span className="text-slate-500">flag by</span>
                <span className="font-semibold">{it.by}</span>
                {it.at && <span className="text-slate-400 ml-auto">{new Date(it.at).toLocaleString()}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const TAB_CONFIG = [
  { key: "brief", label: "AI Brief", icon: Sparkles },
  { key: "evaluation", label: "Evaluation", icon: Sparkles },
  { key: "milestones", label: "Milestones", icon: Calendar },
  { key: "spend", label: "Spend & Invoices", icon: DollarSign },
  { key: "approvals", label: "Approvals", icon: CheckCircle2 },
  { key: "redflags", label: "Red Flags", icon: Flag },
  { key: "checklist", label: "Checklist", icon: ClipboardList },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "comments", label: "Comments & Activity", icon: MessageSquare },
  { key: "notes", label: "Notes & Messages", icon: FileText },
  { key: "timeline", label: "Timeline", icon: Activity },
  { key: "timers", label: "Timers & Amendments", icon: History },
  { key: "original", label: "Original Submission", icon: FileDown },
  { key: "details", label: "Full Details", icon: History },
];

const CLASSIFICATION_COLORS = {
  "Public Record": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Internal":      "bg-slate-100 text-slate-700 border-slate-200",
  "Confidential":  "bg-amber-50 text-amber-800 border-amber-200",
  "Restricted":    "bg-red-50 text-red-700 border-red-200",
};

function ClassificationChip({ d, contractId, canEdit, onChange }) {  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const cls = d.classification || "Internal";
  const set = async (next) => {
    setBusy(true);
    try {
      await api.patch(`/contracts/${contractId}/documents/${d.id}/classification`, { classification: next });
      toast.success(`Classification → ${next}`);
      setOpen(false);
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  if (!canEdit) {
    return <span data-testid={`doc-cls-${d.id}`} className={`text-[10px] px-2 py-0.5 rounded-full border ${CLASSIFICATION_COLORS[cls]}`}>{cls}</span>;
  }
  return (
    <div className="relative">
      <button data-testid={`doc-cls-${d.id}`} onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
              className={`text-[10px] px-2 py-0.5 rounded-full border ${CLASSIFICATION_COLORS[cls]} hover:opacity-80`}>{cls} ▾</button>
      {open && (
        <div className="absolute right-0 top-6 z-10 bg-white border border-slate-200 rounded-md shadow-lg min-w-[160px]" onClick={(e) => e.stopPropagation()}>
          {Object.keys(CLASSIFICATION_COLORS).map(k => (
            <button key={k} disabled={busy} data-testid={`doc-cls-${d.id}-set-${k.replace(/\s+/g,'-')}`}
                    onClick={() => set(k)}
                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 ${cls === k ? "font-bold" : ""}`}>
              {k}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MilestonesTab({ contractId }) {
  const { user } = useAuth();
  const canEdit = ["it_admin","general_counsel","legal_manager","attorney","contract_analyst","compliance","contract_manager"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");
  const [kind, setKind] = useState("custom");
  const [due, setDue] = useState("");
  const [notes, setNotes] = useState("");

  const load = () => api.get(`/contracts/${contractId}/milestones`).then(r => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, [contractId]);

  const add = async () => {
    if (!label.trim() || !due) { toast.error("Label + due date required"); return; }
    setBusy(true);
    try {
      await api.post(`/contracts/${contractId}/milestones`, { label: label.trim(), kind, due_date: due, notes });
      toast.success("Milestone added"); setLabel(""); setKind("custom"); setDue(""); setNotes(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const toggleDone = async (m) => {
    try { await api.patch(`/contracts/${contractId}/milestones/${m.id}`, { done: !m.done }); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (m) => {
    if (!window.confirm(`Delete milestone "${m.label}"?`)) return;
    try { await api.delete(`/contracts/${contractId}/milestones/${m.id}`); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="milestones-tab">
      {canEdit ? (
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-[#005289] mb-2">Add Milestone</div>
          <div className="grid grid-cols-4 gap-2 text-xs">
            <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Label (e.g. Q1 Deliverable)"
                   className="h-9 px-3 border border-slate-200 rounded col-span-2" data-testid="ms-label"/>
            <select value={kind} onChange={e => setKind(e.target.value)} className="h-9 px-2 border border-slate-200 rounded" data-testid="ms-kind">
              <option value="custom">Custom</option>
              <option value="deliverable">Deliverable</option>
              <option value="payment">Payment</option>
              <option value="review">Review</option>
              <option value="renewal_notice">Renewal Notice</option>
              <option value="board_approval">Board Approval</option>
              <option value="insurance_check">Insurance Check</option>
            </select>
            <input type="date" value={due} onChange={e => setDue(e.target.value)} className="h-9 px-2 border border-slate-200 rounded" data-testid="ms-due"/>
          </div>
          <div className="flex gap-2 mt-2">
            <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)"
                   className="h-9 px-3 border border-slate-200 rounded flex-1 text-xs" data-testid="ms-notes"/>
            <button onClick={add} disabled={busy} data-testid="ms-add"
                    className="h-9 px-4 rounded text-xs font-semibold bg-[#005289] text-white disabled:opacity-60">Add</button>
          </div>
        </div>
      ) : null}

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="px-4 py-2 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">Milestones ({rows.length})</div>
        {rows.length === 0 ? <div className="p-6 text-center text-xs text-slate-400">No milestones yet.</div> : null}
        <div className="divide-y divide-slate-100">
          {rows.map(m => {
            const now = new Date();
            const dueDt = m.due_date ? new Date(m.due_date + "T00:00:00") : null;
            const days = dueDt ? Math.round((dueDt - now) / 86400000) : null;
            const chip = m.done ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                     : days == null ? "bg-slate-50 text-slate-600 border-slate-200"
                     : days < 0 ? "bg-red-50 text-red-700 border-red-200"
                     : days <= 7 ? "bg-amber-50 text-amber-700 border-amber-200"
                     : "bg-slate-50 text-slate-600 border-slate-200";
            return (
              <div key={m.id} className="px-4 py-3 flex items-center gap-3" data-testid={`ms-row-${m.id}`}>
                <button onClick={() => toggleDone(m)} data-testid={`ms-check-${m.id}`}
                        className={`w-5 h-5 rounded border ${m.done ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-slate-300"} flex items-center justify-center`}>
                  {m.done ? <Check className="w-3 h-3"/> : null}
                </button>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold text-slate-900 ${m.done ? "line-through opacity-60" : ""}`}>{m.label}</div>
                  <div className="text-[11px] text-slate-500">{m.kind.replace(/_/g, " ")} · {m.due_date}{m.notes ? ` · ${m.notes}` : ""}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 border rounded font-semibold ${chip}`}>
                  {m.done ? "Done" : days == null ? "—" : days < 0 ? `${-days}d overdue` : `${days}d`}
                </span>
                {canEdit ? <button onClick={() => del(m)} data-testid={`ms-del-${m.id}`} className="text-slate-400 hover:text-red-600 text-xs">✕</button> : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function EvaluationTab({ contractId, contract, onChanged }) {
  const { user } = useAuth();
  const [evals, setEvals] = useState([]);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [openId, setOpenId] = useState(null);
  const canReview = ["general_counsel", "legal_manager", "attorney", "it_admin"].includes(user?.role);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/evaluations?contract_id=${contractId}`);
      setEvals(data || []);
    } catch { /* noop */ } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [contractId]);

  const runNow = async () => {
    setRunning(true);
    try {
      await api.post(`/evaluate/contract/${contractId}`, {}, { timeout: 180000 });
      toast.success("Evaluation complete");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Evaluation failed");
    } finally { setRunning(false); }
  };

  const finalize = async (evalId, status) => {
    const note = window.prompt(`Add a note for ${status} (optional):`, "") || "";
    try {
      await api.post(`/evaluations/${evalId}/finalize`, { status, note });
      toast.success(`Evaluation ${status}`);
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const opened = evals.find(e => e.id === openId);

  return (
    <div className="space-y-4" data-testid="evaluation-tab">
      <div className="bg-white border border-slate-200 rounded-md p-4 flex items-start gap-3">
        <div className="flex-1">
          <div className="text-xs font-bold uppercase tracking-wider text-[#005289]">AI Contract Evaluator</div>
          <div className="text-sm text-slate-700 mt-1">
            Latest risk: <span className="font-semibold">{contract?.latest_evaluation_risk || "—"}</span>
            {contract?.latest_evaluation_score != null ? <> · Score <span className="font-semibold">{contract.latest_evaluation_score}/100</span></> : null}
            {contract?.latest_evaluation_status ? <> · Status <span className="font-semibold">{contract.latest_evaluation_status}</span></> : null}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">Preliminary output — subject to Legal Department review.</div>
        </div>
        <button onClick={runNow} disabled={running} data-testid="eval-run-now"
                className="h-9 px-4 rounded-md text-xs font-semibold bg-[#005289] text-white disabled:opacity-50">
          {running ? "Running…" : "Run evaluation now"}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="px-4 py-2 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">Evaluation history ({evals.length})</div>
        {busy && !evals.length ? <div className="p-6 text-center text-xs text-slate-400">Loading…</div> : null}
        {!busy && !evals.length ? <div className="p-6 text-center text-xs text-slate-400">No evaluations yet. Click "Run evaluation now" to start.</div> : null}
        <div className="divide-y divide-slate-100">
          {evals.map(e => {
            const r = e.result || {};
            const riskCls = r.overall_risk === "High" ? "bg-red-50 text-red-700 border-red-200" :
                            r.overall_risk === "Moderate" ? "bg-amber-50 text-amber-700 border-amber-200" :
                            "bg-emerald-50 text-emerald-700 border-emerald-200";
            const statusCls = e.status === "Accepted" ? "text-emerald-700" :
                              e.status === "Rejected" ? "text-red-700" :
                              e.status === "Modified" ? "text-amber-700" : "text-slate-500";
            return (
              <div key={e.id} className="px-4 py-3 flex items-center gap-3" data-testid={`eval-row-${e.id}`}>
                <span className={`text-[10px] px-2 py-0.5 border rounded font-semibold ${riskCls}`}>{r.overall_risk || "—"} · {r.risk_score ?? 0}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-700">
                    <span className="font-semibold">{e.source === "auto_submit" ? "Auto (on submit)" : e.source === "auto_upload" ? "Auto (on upload)" : e.source === "contract" ? "Manual" : e.source}</span>
                    {" · "}by {e.user_name || "system"}
                    {" · "}{new Date(e.created_at).toLocaleString()}
                  </div>
                  <div className={`text-[11px] font-semibold ${statusCls}`}>Status: {e.status || "Pending Review"}</div>
                </div>
                <button onClick={() => setOpenId(e.id)} data-testid={`eval-view-${e.id}`} className="h-7 px-3 text-[11px] rounded border border-slate-200 hover:bg-slate-50">View</button>
                {canReview && e.status !== "Accepted" ? (
                  <>
                    <button onClick={() => finalize(e.id, "Accepted")} data-testid={`eval-accept-${e.id}`} className="h-7 px-3 text-[11px] rounded bg-emerald-600 text-white">Accept</button>
                    <button onClick={() => finalize(e.id, "Modified")} data-testid={`eval-modify-${e.id}`} className="h-7 px-3 text-[11px] rounded bg-amber-500 text-white">Modify</button>
                    <button onClick={() => finalize(e.id, "Rejected")} data-testid={`eval-reject-${e.id}`} className="h-7 px-3 text-[11px] rounded bg-red-600 text-white">Reject</button>
                  </>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {opened ? <EvaluationResultModal r={opened} onClose={() => setOpenId(null)} /> : null}
    </div>
  );
}

function OriginalSubmissionTab({ contractId }) {
  const [snap, setSnap] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/contracts/${contractId}/original-submission`)
       .then(r => setSnap(r.data)).catch(e => setErr(e?.response?.data?.detail || "Failed to load"));
  }, [contractId]);
  if (err) return <div className="text-sm text-red-700" data-testid="original-error">{err}</div>;
  if (!snap) return <div className="text-sm text-slate-500">Loading snapshot…</div>;
  const os = snap.original_submission;
  if (!os) return <div className="text-sm text-slate-500" data-testid="original-empty">No original submission recorded (pre-Phase-3 contract).</div>;
  const versions = snap.versions || [];
  const groups = [
    { title: "Submitter", keys: ["submitter_first_name","submitter_last_name","submitter_email","business_unit","department","on_behalf_of_user_id"] },
    { title: "Vendor", keys: ["vendor_legal_name","vendor_dba","vendor_contact_name","vendor_contact_email","vendor_contact_phone","vendor_id"] },
    { title: "Contract", keys: ["contract_type","term_length_preferred","timeline","effective_date","expiration_date","auto_renewal","renewal_notice_days","termination_notice_days"] },
    { title: "Financial", keys: ["contract_amount","one_time_cost","capital_cost","po_number","is_budgeted","funding_source","budget_line","cost_center"] },
    { title: "Compliance", keys: ["baa_required","insurance_required","handles_phi_pii_pci","it_resources_needed","board_approval_required"] },
    { title: "Purpose", keys: ["purpose","description"] },
  ];
  return (
    <div className="space-y-4" data-testid="tab-original-body">
      <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3">
        Frozen at <span className="font-mono">{snap.original_submission_at || "—"}</span>. Read-only — any changes after submission are recorded in Versions.
      </div>
      {groups.map(g => (
        <div key={g.title} className="border border-slate-200 rounded-md">
          <div className="px-4 py-2 bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-600 border-b border-slate-200">{g.title}</div>
          <div className="p-4 grid grid-cols-2 gap-3 text-xs">
            {g.keys.filter(k => os[k] !== undefined && os[k] !== "" && os[k] !== null).map(k => (
              <div key={k}><div className="text-[10px] uppercase text-slate-400 tracking-wider">{k}</div>
                <div className="text-slate-800 break-words">{typeof os[k] === "boolean" ? (os[k] ? "Yes" : "No") : String(os[k])}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
      {versions.length > 0 && (
        <div className="border border-slate-200 rounded-md">
          <div className="px-4 py-2 bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-600 border-b border-slate-200">Versions ({versions.length})</div>
          <div className="p-4 space-y-2 text-xs">
            {versions.map((v, i) => (
              <div key={i} className="border-l-2 border-slate-200 pl-3">
                <div className="text-slate-500">{v.at} — {v.actor}</div>
                <pre className="text-[11px] whitespace-pre-wrap">{JSON.stringify(v.field_changes || v, null, 2)}</pre>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ContractDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [team, setTeam] = useState([]);
  const [tab, setTab] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const t = p.get("tab");
      if (t && TAB_CONFIG.some(x => x.key === t)) return t;
    } catch { /* noop */ }
    return "brief";
  });
  const [commentBody, setCommentBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [pendingMentions, setPendingMentions] = useState([]);
  const [timers, setTimers] = useState([]);
  const [newTimerLabel, setNewTimerLabel] = useState("");
  const [newTimerDate, setNewTimerDate] = useState("");
  const [amendments, setAmendments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [newNoteFile, setNewNoteFile] = useState(null);
  const [newNoteExternal, setNewNoteExternal] = useState(false);
  const [newNoteVisibility, setNewNoteVisibility] = useState("");
  const [noteMentions, setNoteMentions] = useState([]);
  const [noteResetTick, setNoteResetTick] = useState(0);
  const [linkModal, setLinkModal] = useState(false);
  const [linkChildrenModal, setLinkChildrenModal] = useState(false);
  const [msgTarget, setMsgTarget] = useState(null);
  const [vendorMessages, setVendorMessages] = useState([]);
  const [vendorReplyBody, setVendorReplyBody] = useState("");
  const [sigEmail, setSigEmail] = useState("");
  const [sigName, setSigName] = useState("");
  const [compareHighOnly, setCompareHighOnly] = useState(false);
  const [priorityOpen, setPriorityOpen] = useState(false);
  const [priorityReason, setPriorityReason] = useState("");
  const [priorityDate, setPriorityDate] = useState("");
  const [vendorLinkOpen, setVendorLinkOpen] = useState(false);
  const [vlName, setVlName] = useState("");
  const [vlEmail, setVlEmail] = useState("");
  const [vlCc, setVlCc] = useState("");
  const [vlMsg, setVlMsg] = useState("");
  const [correspondence, setCorrespondence] = useState([]);
  const [timeline, setTimeline] = useState(null);
  const [executeModal, setExecuteModal] = useState(false);
  const [execFile, setExecFile] = useState(null);
  const [execEffective, setExecEffective] = useState("");
  const [execEnd, setExecEnd] = useState("");
  const [execBusy, setExecBusy] = useState(false);
  const [execStep, setExecStep] = useState("");     // "" | "uploading" | "verifying" | "executing"
  const [sigResult, setSigResult] = useState(null);
  const [overrideMissing, setOverrideMissing] = useState(false);
  const commentRef = useRef(null);

  const canAssign = ["it_admin","legal_manager","general_counsel"].includes(user?.role);
  const canLegal = ["general_counsel","legal_manager","attorney","contract_analyst","it_admin"].includes(user?.role);
  // Editing compliance/red flags is broader than canLegal — Compliance officers
  // are explicitly included per policy alongside Attorney and GC.
  const canFlags = ["general_counsel","legal_manager","attorney","contract_analyst","compliance","it_admin"].includes(user?.role);
  const canExecute = ["general_counsel","legal_manager","it_admin"].includes(user?.role);
  const canCompliance = ["compliance","it_admin","general_counsel"].includes(user?.role);
  const canClearCompliance = ["compliance","it_admin"].includes(user?.role);

  const reload = async () => {
    try { const r = await api.get(`/contracts/${id}`); setC(r.data); } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { reload(); api.get("/users/team").then(r => setTeam(r.data)).catch(()=>{}); }, [id]);

  useEffect(() => {
    if (!id) return;
    api.get(`/contracts/${id}/timers`).then(r => setTimers(r.data || [])).catch(() => {});
    api.get(`/contracts/${id}/amendments`).then(r => setAmendments(r.data || [])).catch(() => {});
    api.get(`/contracts/${id}/notes`).then(r => setNotes(r.data || [])).catch(() => {});
    api.get(`/contracts/${id}/vendor-messages`).then(r => setVendorMessages(r.data || [])).catch(() => {});
  }, [id, c?.updated_at]);

  // Deep-link: /contract/:id?tab=notes&note=<id> — activate the tab and scroll
  // to the exact note (used by bell notifications from @-mentions).
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const noteId = p.get("note");
      if (!noteId || notes.length === 0) return;
      if (tab !== "notes") setTab("notes");
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-testid="note-${noteId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-[#005289]", "ring-offset-2");
          setTimeout(() => el.classList.remove("ring-2", "ring-[#005289]", "ring-offset-2"), 2200);
        }
      });
    } catch { /* noop */ }
  }, [notes, tab]);

  const unreadVendorMsgs = vendorMessages.filter(m => m.from === "vendor" && !(c?.vendor_messages_read_by?.[user?.id]) || (c?.vendor_messages_read_by?.[user?.id] && new Date(m.at) > new Date(c.vendor_messages_read_by[user.id]))).length;

  const addNote = async () => {
    if (!newNote.trim() && !newNoteFile) return toast.error("Enter a note or attach a file");
    const fd = new FormData();
    fd.append("body", newNote);
    fd.append("external_visible", newNoteExternal ? "true" : "false");
    if (newNoteVisibility) fd.append("visibility", newNoteVisibility);
    if (newNoteFile) fd.append("file", newNoteFile);
    if (noteMentions.length) fd.append("mentions", noteMentions.map(m => m.id).join(","));
    try {
      await api.post(`/contracts/${id}/notes`, fd);
      setNewNote(""); setNewNoteFile(null); setNewNoteExternal(false); setNewNoteVisibility(""); setNoteMentions([]); setNoteResetTick(t => t + 1);
      const r = await api.get(`/contracts/${id}/notes`);
      setNotes(r.data);
      toast.success(noteMentions.length ? `Note added · ${noteMentions.length} notified` : "Note added");
    } catch (e) { toast.error("Failed"); }
  };

  const sendVendorMsg = async () => {
    if (!vendorReplyBody.trim()) return;
    try {
      await api.post(`/contracts/${id}/vendor-messages`, { body: vendorReplyBody });
      setVendorReplyBody("");
      const r = await api.get(`/contracts/${id}/vendor-messages`);
      setVendorMessages(r.data);
    } catch (e) { toast.error("Send failed"); }
  };

  const markVendorRead = async () => {
    try { await api.post(`/contracts/${id}/mark-vendor-messages-read`); reload(); } catch {}
  };

  const requestSignature = async () => {
    if (!sigEmail || !sigName) return toast.error("Signer name and email required");
    try {
      const { data } = await api.post(`/contracts/${id}/signature-requests`, {
        signer_email: sigEmail, signer_name: sigName, signer_role: "General Counsel"
      });
      toast.success("Signature request emailed — MOCK e-sig");
      window.prompt("Signing link (share via email/text):", data.url);
      setSigEmail(""); setSigName("");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const highOpenFlags = useMemo(() => (c?.red_flags || []).filter(f => f.severity === "High" && !f.resolved && !f.accepted_by_gc), [c]);

  useEffect(() => {
    if (tab === "timers") {
      api.get(`/contracts/${id}/timers`).then(r => setTimers(r.data || [])).catch(()=>{});
      api.get(`/contracts/${id}/amendments`).then(r => setAmendments(r.data || [])).catch(()=>{});
    }
    if (tab === "notes") {
      api.get(`/contracts/${id}/notes`).then(r => setNotes(r.data || [])).catch(()=>{});
      api.get(`/contracts/${id}/vendor-messages`).then(r => setVendorMessages(r.data || [])).catch(()=>{});
    }
  }, [tab, id]);

  const genBrief = async () => {
    setBusy(true);
    try {
      toast.info("Generating Director's Brief with Claude Sonnet 5...");
      await api.post(`/contracts/${id}/generate-brief`);
      toast.success("Brief generated");
      reload();
    } catch (e) { toast.error("Brief generation failed"); }
    finally { setBusy(false); }
  };

  const assign = async (which, uid) => {
    try { await api.post(`/contracts/${id}/assign`, { [which]: uid }); toast.success("Assigned"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
  };

  const transition = async (stage) => {
    try {
      if (stage === "executed") {
        // Open modal so the user uploads the fully-executed file first.
        setExecuteModal(true);
        return;
      }
      await api.post(`/contracts/${id}/stage`, { stage });
      toast.success(`Moved to ${STAGE_LABELS[stage]}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Stage change blocked"); }
  };

  const executeAndFile = async () => {
    // Check if an executed doc already exists on the contract.
    const hasExec = (c?.documents || []).some(d => ["executed","signed_final","fully_executed"].includes((d.kind || "").toLowerCase()));
    if (!hasExec && !execFile) {
      toast.error("Please attach the fully-executed contract file.");
      return;
    }
    setExecBusy(true);
    try {
      if (execFile) {
        setExecStep("uploading");
        const fd = new FormData();
        fd.append("file", execFile);
        fd.append("kind", "executed");
        fd.append("classification", "Internal");
        await api.post(`/contracts/${id}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
        });
      }
      // AI signature verification — must pass unless user explicitly overrides.
      setExecStep("verifying");
      let sig = null;
      try {
        const { data } = await api.post(`/contracts/${id}/verify-signatures`, {}, { timeout: 180000 });
        sig = data;
        setSigResult(data);
      } catch (e) {
        // If verification errored, surface but still allow override.
        setSigResult({ error: e?.response?.data?.detail || "Verification failed" });
      }
      if (sig && !sig.all_signatures_present && !overrideMissing) {
        setExecBusy(false); setExecStep("");
        toast.error("Signature verification failed — review missing items or check 'Execute anyway'");
        return;
      }
      setExecStep("executing");
      await api.post(`/contracts/${id}/execute`, {
        effective_date: execEffective || null,
        end_date: execEnd || null,
      });
      toast.success("Contract executed — filed in HCD Contract Central");
      setExecuteModal(false);
      setExecFile(null);
      setExecEffective("");
      setExecEnd("");
      setSigResult(null);
      setOverrideMissing(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Execution failed");
    } finally { setExecBusy(false); setExecStep(""); }
  };

  const addComment = async () => {
    if (!commentBody.trim()) return;
    try {
      await api.post(`/contracts/${id}/comments`, {
        body: commentBody,
        internal: true,
        mentions: pendingMentions.map(m => m.id),
      });
      setCommentBody("");
      setPendingMentions([]);
      reload();
    } catch (e) { toast.error("Failed"); }
  };

  const onCommentChange = (val) => {
    setCommentBody(val);
    // detect @-trigger
    const before = val.slice(0, commentRef.current?.selectionStart ?? val.length);
    const m = before.match(/@([\w-]{0,30})$/);
    if (m) {
      setMentionOpen(true);
      setMentionQuery(m[1].toLowerCase());
    } else {
      setMentionOpen(false);
    }
  };

  const insertMention = (u) => {
    const val = commentBody;
    const cursor = commentRef.current?.selectionStart ?? val.length;
    const before = val.slice(0, cursor);
    const after = val.slice(cursor);
    const rep = before.replace(/@[\w-]{0,30}$/, `@${u.name} `);
    setCommentBody(rep + after);
    setPendingMentions(prev => (prev.find(x => x.id === u.id) ? prev : [...prev, u]));
    setMentionOpen(false);
    setTimeout(() => commentRef.current?.focus(), 0);
  };

  const filteredMentions = team.filter(u => u.name.toLowerCase().includes(mentionQuery));

  const addTimer = async () => {
    if (!newTimerLabel.trim() || !newTimerDate) return toast.error("Label and date required");
    try {
      await api.post(`/contracts/${id}/timers`, { label: newTimerLabel.trim(), due_date: newTimerDate });
      setNewTimerLabel(""); setNewTimerDate("");
      const r = await api.get(`/contracts/${id}/timers`);
      setTimers(r.data);
      toast.success("Reminder set");
    } catch (e) { toast.error("Failed"); }
  };
  const removeTimer = async (tid) => {
    try { await api.delete(`/contracts/${id}/timers/${tid}`); setTimers(prev => prev.filter(t => t.id !== tid)); }
    catch { toast.error("Failed"); }
  };

  const createAmendment = async () => {
    const description = window.prompt("Amendment description (what is changing?):");
    if (!description?.trim()) return;
    const amountStr = window.prompt("New contract amount (leave blank to keep current):", "");
    const body = { description: description.trim() };
    if (amountStr && !isNaN(parseFloat(amountStr))) body.contract_amount = parseFloat(amountStr);
    try {
      const { data } = await api.post(`/contracts/${id}/amendments`, body);
      toast.success(`Amendment ${data.contract_number} created`);
      const r = await api.get(`/contracts/${id}/amendments`);
      setAmendments(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create amendment"); }
  };

  const issueVendorLink = async (payload) => {
    try {
      const { data } = await api.post(`/contracts/${id}/vendor-link`, payload || {});
      await navigator.clipboard.writeText(data.url).catch(() => {});
      const to = (data.delivered_to || []).map(d => d.email).join(", ");
      toast.success(`Vendor link sent${to ? " to " + to : ""} & copied to clipboard`);
      reload();
      return data;
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const approveAmendment = async () => {
    try {
      await api.post(`/contracts/${id}/amendments/approve`);
      toast.success("Amendment approved");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const canPriority = ["department_director", "legal_manager", "general_counsel", "it_admin"].includes(user?.role);

  const openPriorityModal = () => {
    setPriorityReason(c?.priority?.reason || "");
    setPriorityDate(c?.priority?.needed_by || "");
    setPriorityOpen(true);
  };

  const savePriority = async () => {
    if (!priorityReason.trim()) return toast.error("Priority reason is required");
    try {
      await api.post(`/contracts/${id}/priority`, {
        is_priority: true,
        reason: priorityReason.trim(),
        needed_by: priorityDate || null,
      });
      toast.success("Contract flagged as PRIORITY — team notified");
      setPriorityOpen(false);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const clearPriority = async () => {
    if (!window.confirm("Clear the priority flag on this contract?")) return;
    try {
      await api.post(`/contracts/${id}/priority`, { is_priority: false });
      toast.success("Priority flag cleared");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openVendorLinkModal = () => {
    setVlName(c.vendor_contact_name || c.vendor_legal_name || "");
    setVlEmail(c.vendor_contact_email || "");
    setVlCc("");
    setVlMsg("");
    setVendorLinkOpen(true);
  };

  const sendVendorLink = async () => {
    const cc = vlCc.split(/[,;\s]+/).map(s => s.trim()).filter(s => s.includes("@"));
    if (!vlEmail && cc.length === 0) return toast.error("Provide at least one recipient email");
    const data = await issueVendorLink({
      vendor_name: vlName.trim(),
      vendor_email: vlEmail.trim(),
      cc_emails: cc,
      message: vlMsg.trim(),
    });
    if (data) setVendorLinkOpen(false);
  };

  const filterCompareContent = (md, onlyHigh) => {
    if (!md || !onlyHigh) return md;
    const lines = md.split("\n");
    const out = [];
    let inTable = false;
    let headerBuf = [];
    for (const ln of lines) {
      const s = ln.trim();
      const isRow = s.startsWith("|");
      if (isRow) {
        if (!inTable) {
          headerBuf.push(ln);
          if (headerBuf.length >= 2) { out.push(...headerBuf); inTable = true; headerBuf = []; }
          continue;
        }
        const cells = s.split("|").map(c => c.trim());
        const sev = cells[4] || "";
        if (/high/i.test(sev) || sev.includes("🚩")) out.push(ln);
      } else {
        if (headerBuf.length) { headerBuf = []; }
        if (inTable) inTable = false;
        out.push(ln);
      }
    }
    return out.join("\n");
  };

  const downloadHighRiskMemo = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("hcd_token") || "";
      const res = await fetch(`${BACKEND_URL}/api/contracts/${id}/ai-compare/memo`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `high_risk_memo_${c.contract_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("High-Risk memo downloaded");
    } catch (e) { toast.error("Memo export failed"); }
  };

  const runCompare = async () => {
    const docs = (c.documents || []).filter(d => d.kind === "main" || d.kind === "vendor_edit");
    if (docs.length < 2) return toast.error("Need at least 2 documents");
    const main = docs.filter(d => d.kind === "main").pop() || docs[0];
    const rev = docs.filter(d => d.kind === "vendor_edit").pop() || docs[docs.length - 1];
    if (main.id === rev.id) return toast.error("Pick two different documents");
    setBusy(true);
    try {
      toast.info("Claude is comparing...");
      await api.post(`/contracts/${id}/ai-compare`, { original_document_id: main.id, revised_document_id: rev.id });
      toast.success("AI compare complete");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };


  const uploadDoc = async (kind, file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("kind", kind);
    try { await api.post(`/contracts/${id}/upload`, fd); toast.success("Uploaded"); reload(); }
    catch { toast.error("Upload failed"); }
  };

  const previewDoc = async (docId) => {
    try {
      const { data } = await api.get(`/contracts/${id}/documents/${docId}`);
      // Build data URL from base64
      const isPdf = (data.content_type || "").includes("pdf") || (data.filename || "").toLowerCase().endsWith(".pdf");
      if (!isPdf) return toast.info("Preview supports PDF only. Non-PDF files can still be downloaded.");
      const url = `data:${data.content_type || "application/pdf"};base64,${data.data_b64}`;
      setPdfPreview({ url, filename: data.filename });
    } catch (e) { toast.error("Failed to load document"); }
  };

  const raiseComplianceFlag = async () => {
    const note = window.prompt("Describe the compliance concern (BAA gap, PHI exposure, OIG hit, etc.)");
    if (!note?.trim()) return;
    try {
      const fd = new FormData(); fd.append("note", note.trim());
      await api.post(`/contracts/${id}/compliance-flag`, fd);
      toast.success("Compliance flag raised — execution is now blocked until cleared");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to raise flag"); }
  };

  const clearComplianceFlag = async (fid) => {
    try {
      await api.post(`/contracts/${id}/compliance-flag/${fid}/clear`);
      toast.success("Compliance flag cleared");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setChecklist = async (key, completed) => {
    try { await api.post(`/contracts/${id}/checklist`, { key, completed, notes: "" }); reload(); }
    catch { toast.error("Failed"); }
  };

  const addFlag = async () => {
    const type = window.prompt("Red Flag type (arbitration, auto_renewal, cpi_escalator, termination_restriction, indemnification, insurance_gap, phi_baa, non_standard_payment, custom):", "auto_renewal");
    if (!type) return;
    const sev = window.prompt("Severity (Low, Medium, High):", "Medium") || "Medium";
    const notes = window.prompt("Notes / clause reference:", "");
    try { await api.post(`/contracts/${id}/red-flags`, { flag_type: type, severity: sev, notes, value: "Yes" }); reload(); }
    catch (e) { toast.error("Failed"); }
  };

  const updateFlag = async (fid, patch) => {
    try { await api.patch(`/contracts/${id}/red-flags/${fid}`, patch); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const downloadPacket = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("hcd_token") || "";
      const res = await fetch(`${BACKEND_URL}/api/contracts/${id}/execution-packet`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `execution_packet_${c.contract_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Execution packet downloaded");
    } catch (e) {
      toast.error("Failed to generate packet");
    }
  };

  const nextStage = c ? STAGES_ORDER[STAGES_ORDER.indexOf(c.stage) + 1] : null;

  if (!c) {
    return <div className="p-6 text-slate-600" data-testid="contract-detail-loading">Loading contract…</div>;
  }


  return (
    <div data-testid="contract-detail-page">
      {/* Header bar */}
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
              <button onClick={() => nav("/pipeline")} className="hover:underline">Pipeline</button>
              <ChevronRight className="w-3 h-3" />
              <span className="font-mono">{c.contract_number}</span>
            </div>
            <h1 className="text-xl font-bold text-slate-900">{c.vendor_legal_name}</h1>
            <div className="text-sm text-slate-500">{c.description}</div>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="chip chip-draft"><span className="dot" />{c.department}</span>
              <span className="chip chip-draft"><span className="dot" />{c.contract_type}</span>
              <span className="chip chip-draft"><span className="dot" />{c.timeline}</span>
              <SLABadge status={c.sla_status} remaining={c.sla_remaining} />
              {c.priority?.is_priority && (
                <span className="chip chip-overdue" data-testid="priority-badge" title={c.priority.reason}>
                  <span className="dot" />PRIORITY{c.priority.needed_by ? ` · needed by ${c.priority.needed_by}` : ""}
                </span>
              )}
              {c.executive_sponsor_name && (
                <span className="chip chip-completed" data-testid="exec-sponsor-chip"
                      title={`Executive Sponsor: ${c.executive_sponsor_name}${c.executive_sponsor_title ? ` (${c.executive_sponsor_title})` : ""}${c.executive_sponsor_email ? ` · ${c.executive_sponsor_email}` : ""}`}>
                  <span className="dot" />Sponsor: {c.executive_sponsor_name}
                </span>
              )}
              {c.bu_approver_name && (
                <span className="chip chip-ontrack" data-testid="bu-approver-chip"
                      title={`BU Approver: ${c.bu_approver_name}${c.bu_approver_email ? ` · ${c.bu_approver_email}` : ""}`}>
                  <span className="dot" />Approver: {c.bu_approver_name}
                </span>
              )}
              {c.is_restricted && (
                <span className="chip chip-overdue" data-testid="restricted-chip"
                      title={`Restricted — ${c.restricted_reason || 'confidential'}. Only Legal + granted users can view.`}>
                  <span className="dot" />🔒 RESTRICTED
                </span>
              )}
              {c.expired_display && (
                <span className="chip chip-overdue" data-testid="expired-chip"
                      title={`Expired ${c.days_since_expiration} day(s) ago`}>
                  <span className="dot" />EXPIRED
                </span>
              )}
              {c.auto_renewal && (
                <span className="chip chip-completed" data-testid="auto-renew-chip">
                  <span className="dot" />Auto-Renew
                </span>
              )}
              {c.parent_contract_number && (
                <span className="chip chip-draft" data-testid="parent-link-chip"
                      title={`Amendment of ${c.parent_contract_number}`}>
                  <span className="dot" />Amendment of #{c.parent_contract_number}
                </span>
              )}
              {highOpenFlags.length > 0 && <span className="chip chip-overdue" data-testid="high-flag-warn"><span className="dot" />{highOpenFlags.length} High Flag{highOpenFlags.length>1?"s":""}</span>}
              {c.compliance_cleared?.cleared && (
                <span className="chip chip-ontrack" data-testid="compliance-cleared-chip"
                      title={`Cleared ${new Date(c.compliance_cleared.cleared_at).toLocaleDateString()} by ${c.compliance_cleared.cleared_by_name}`}>
                  <span className="dot" />Compliance Cleared
                </span>
              )}
              {c.stage === "executed" && c.compliance_cleared?.cleared && (
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/contracts/${id}/board-ready-bundle`}
                   target="_blank" rel="noopener noreferrer"
                   className="chip chip-completed cursor-pointer hover:opacity-80"
                   data-testid="board-ready-btn"
                   title="Download the one-page Board-Ready cover PDF">
                  <FileDown className="w-3 h-3" /> Board-Ready PDF
                </a>
              )}
            </div>
            <SnoozeAuditPanel contract={c} contractId={id} />
            <PeopleStrip contract={c} onMessage={setMsgTarget} />
            <LinkedContractsPanel contractId={id} onLinkParent={() => setLinkModal(true)} onLinkChildren={() => setLinkChildrenModal(true)} canLink={user && (["contract_analyst","attorney","legal_manager","general_counsel","it_admin"].includes(user.role))} />
            <FamilyTimeline contractId={id} contract={c} />
            <LinkParentModal open={linkModal} contract={c} onClose={() => setLinkModal(false)} onDone={() => window.location.reload()} />
            <LinkChildrenModal open={linkChildrenModal} parentContract={c} onClose={() => setLinkChildrenModal(false)} onDone={() => window.location.reload()} />
            <MessagePersonModal open={!!msgTarget} contract={c} target={msgTarget} onClose={() => setMsgTarget(null)} />
            {(user?.role === "compliance" || user?.role === "general_counsel" || user?.role === "it_admin") && (
              <div className="mt-3 flex items-center gap-2">
                {!c.compliance_cleared?.cleared ? (
                  <button
                    onClick={async () => {
                      const notes = window.prompt("Compliance clearance notes (optional):", "") || "";
                      const term = window.confirm("Is termination intended for this contract? Click OK to also auto-snooze ALL pending renewal alerts, or Cancel to keep alerts active.");
                      try {
                        const r = await api.post(`/contracts/${id}/compliance-clear`, { notes, termination_intended: term });
                        const extra = r.data?.auto_snoozed_flags ? ` · ${r.data.auto_snoozed_flags} renewal flag(s) auto-snoozed` : "";
                        toast.success("Compliance Cleared stamped" + extra);
                        reload();
                      } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                    }}
                    className="h-7 px-3 text-[11px] font-semibold rounded border border-emerald-300 text-emerald-800 hover:bg-emerald-50 flex items-center gap-1.5"
                    data-testid="compliance-clear-btn"><Check className="w-3 h-3" /> Mark Compliance Cleared</button>
                ) : (
                  <button
                    onClick={async () => {
                      if (!window.confirm("Revoke Compliance Cleared stamp?")) return;
                      try {
                        await api.post(`/contracts/${id}/compliance-unclear`);
                        toast.success("Compliance Cleared revoked");
                        reload();
                      } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                    }}
                    className="h-7 px-3 text-[11px] font-semibold rounded border border-slate-300 text-slate-700 hover:bg-slate-50"
                    data-testid="compliance-revoke-btn">Revoke Compliance Cleared</button>
                )}
                {c.compliance_cleared?.notes && (
                  <span className="text-[11px] text-slate-500 italic">"{c.compliance_cleared.notes}"</span>
                )}
              </div>
            )}
          </div>
          <div className="text-right space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Amount</div>
            <div className="text-lg font-bold text-slate-900">{currency(c.contract_amount)}</div>
            <div className="text-xs text-slate-500">Received {c.initial_received_date ? new Date(c.initial_received_date).toLocaleDateString() : "—"}</div>
          </div>
        </div>

        {/* Stage stepper */}
        <div className="mt-4 flex items-center gap-1 overflow-x-auto">
          {STAGES_ORDER.map((s, i) => {
            const idx = STAGES_ORDER.indexOf(c.stage);
            const done = i < idx, current = i === idx;
            return (
              <div key={s} className="flex items-center gap-1 flex-shrink-0">
                <div className={`text-[10px] px-2.5 py-1 rounded-full font-semibold ${current ? "bg-[#005289] text-white" : done ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-500"}`}>
                  {STAGE_LABELS[s]}
                </div>
                {i < STAGES_ORDER.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300" />}
              </div>
            );
          })}
        </div>

        {/* Phase 1 — Rework banner for submitters */}
        <ReworkBanner contract={c} onDone={reload} />

        {/* Phase 3 — Auto-renew 90-day confirmation banner */}
        <RenewalConfirmBanner contract={c} onDone={reload} />

        {/* Phase 3 — Lifecycle chain (master → amendments/renewals with .1/.2 numbering) */}
        <LifecycleChain contractId={id} />

        {/* Actions bar */}
        {canLegal && (
          <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="actions-bar">
            {canAssign && (
              <>
                <select onChange={e => e.target.value && assign("analyst_id", e.target.value)} value={c.assigned_analyst_id || ""}
                        className="h-8 px-2 text-xs border border-slate-200 rounded" data-testid="assign-analyst">
                  <option value="">Assign Analyst...</option>
                  {team.filter(t => t.role === "contract_analyst").map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <select onChange={e => e.target.value && assign("attorney_id", e.target.value)} value={c.assigned_attorney_id || ""}
                        className="h-8 px-2 text-xs border border-slate-200 rounded" data-testid="assign-attorney">
                  <option value="">Assign Attorney...</option>
                  {team.filter(t => t.role === "attorney").map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </>
            )}
            {c.stage !== "executed" && (
              <>
                {/* Phase 1 — Standardised APPROVE / REJECT / REWORK */}
                <ContractActionsBar contractId={id} contract={c} onDone={reload} />
                {c.stage !== "unassigned" && (
                  <button onClick={() => transition("attorney_review")}
                          className="h-8 px-3 text-xs font-semibold border border-slate-200 rounded hover:bg-slate-50" data-testid="btn-attorney-review">→ Attorney Review</button>
                )}
                <button onClick={() => transition("gc_review")}
                        className="h-8 px-3 text-xs font-semibold border border-slate-200 rounded hover:bg-slate-50" data-testid="btn-gc-review">→ GC Review</button>
                <button onClick={() => transition("vendor_review")}
                        className="h-8 px-3 text-xs font-semibold border border-slate-200 rounded hover:bg-slate-50" data-testid="btn-vendor-review">→ Vendor Review</button>
                <button onClick={() => transition("legal_approved")}
                        className="h-8 px-3 text-xs font-semibold border rounded"
                        style={{ background: "#005289", color: "#fff", borderColor: "#005289" }}
                        data-testid="btn-legal-approve">Legal Approve</button>
                {canExecute && c.stage === "legal_approved" && (
                  <button onClick={() => transition("executed")}
                          className="h-8 px-4 text-xs font-bold text-white rounded"
                          style={{ background: "#00AE42" }} data-testid="btn-execute">Execute & File</button>
                )}
              </>
            )}
            <button onClick={downloadPacket}
                    className="h-8 px-3 text-xs font-semibold border rounded flex items-center gap-1.5 hover:bg-slate-50"
                    style={{ borderColor: "#005289", color: "#005289" }}
                    data-testid="btn-download-packet">
              <FileDown className="w-3.5 h-3.5" /> Execution Packet PDF
            </button>
            <button onClick={openVendorLinkModal}
                    className="h-8 px-3 text-xs font-semibold border rounded flex items-center gap-1.5 hover:bg-slate-50"
                    style={{ borderColor: "#00AE42", color: "#00AE42" }}
                    data-testid="btn-vendor-link">
              <ExternalLink className="w-3.5 h-3.5" /> Send Vendor Link
            </button>
            {canPriority && (
              c.priority?.is_priority ? (
                <button onClick={clearPriority}
                        className="h-8 px-3 text-xs font-semibold border rounded flex items-center gap-1.5 hover:bg-slate-50"
                        style={{ borderColor: "#B91C1C", color: "#B91C1C" }}
                        data-testid="btn-clear-priority">
                  <Flag className="w-3.5 h-3.5" /> Clear Priority
                </button>
              ) : (
                <button onClick={openPriorityModal}
                        className="h-8 px-3 text-xs font-semibold text-white rounded flex items-center gap-1.5"
                        style={{ background: "#B91C1C" }}
                        data-testid="btn-mark-priority">
                  <Flag className="w-3.5 h-3.5" /> Mark Priority
                </button>
              )
            )}
            {c.parent_contract_id && user?.role === "general_counsel" && !c.gc_amendment_approved && (
              <button onClick={approveAmendment}
                      className="h-8 px-3 text-xs font-bold text-white rounded flex items-center gap-1.5"
                      style={{ background: "#00AE42" }} data-testid="btn-gc-approve-amendment">
                <CheckCircle2 className="w-3.5 h-3.5" /> Approve Amendment Diff
              </button>
            )}
            {c.parent_contract_id && c.gc_amendment_approved && (
              <span className="chip chip-ontrack"><span className="dot" />Amendment approved by GC</span>
            )}
          </div>
        )}
        {canCompliance && !canLegal && (
          <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="compliance-actions-bar">
            <button onClick={raiseComplianceFlag}
                    className="h-8 px-3 text-xs font-semibold border rounded flex items-center gap-1.5 hover:bg-red-50"
                    style={{ borderColor: "#EF4444", color: "#B91C1C" }}
                    data-testid="btn-raise-compliance-top">
              <Flag className="w-3.5 h-3.5" /> Raise Compliance Flag
            </button>
          </div>
        )}
      </div>

      {/* Tabs + Body */}
      <div className="flex">
        <div className="flex-1 p-6">
          <div className="border-b border-slate-200 mb-4 flex gap-4">
            {TAB_CONFIG.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                      className={`px-1 py-3 text-xs font-semibold border-b-2 flex items-center gap-1.5 ${tab === t.key ? "border-[#005289] text-[#005289]" : "border-transparent text-slate-500 hover:text-slate-900"}`}
                      data-testid={`tab-${t.key}`}>
                <t.icon className="w-3.5 h-3.5" />{t.label}
              </button>
            ))}
          </div>

          {tab === "brief" && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Director's Brief</div>
                  <div className="text-xs text-slate-500">{c.ai_brief ? `Generated ${timeAgo(c.ai_brief.generated_at)} by ${c.ai_brief.generated_by} · ${c.ai_brief.model}` : "No brief yet."}</div>
                </div>
                <div className="flex items-center gap-2">
                  {canLegal && (
                    <button onClick={genBrief} disabled={busy} className="h-8 px-3 text-xs font-semibold text-white rounded flex items-center gap-1.5 disabled:opacity-50"
                            style={{ background: "#005289" }} data-testid="gen-brief-btn">
                      <Sparkles className="w-3.5 h-3.5" />{c.ai_brief ? "Regenerate" : "Generate Brief"}
                    </button>
                  )}
                  {(c.documents || []).filter(d => d.kind === "main" || d.kind === "vendor_edit").length >= 2 && (
                    <button onClick={runCompare} disabled={busy}
                            className="h-8 px-3 text-xs font-semibold text-white rounded flex items-center gap-1.5 disabled:opacity-50"
                            style={{ background: "#00AE42" }} data-testid="ai-compare-btn">
                      <Sparkles className="w-3.5 h-3.5" />AI Compare Original vs Vendor
                    </button>
                  )}
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-md p-6">
                {c.ai_brief ? renderMd(c.ai_brief.content_md) : (
                  <div className="text-center py-12 text-slate-500 text-sm">
                    <Sparkles className="w-8 h-8 mx-auto text-slate-300 mb-3" />
                    No AI brief yet. Upload the vendor's contract and click Generate.
                  </div>
                )}
              </div>
              {(c.ai_compares || []).length > 0 && (
                <div className="mt-6 bg-white border border-slate-200 rounded-md p-6" data-testid="ai-compare-panel">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold uppercase tracking-wider text-[#00AE42]">Latest AI Contract Compare</div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setCompareHighOnly(v => !v)}
                              className={`h-7 px-2 text-[11px] font-semibold rounded border ${compareHighOnly ? "bg-red-50 border-red-300 text-red-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                              data-testid="compare-high-toggle">
                        {compareHighOnly ? "Showing High only" : "Show High-risk only"}
                      </button>
                      <button onClick={downloadHighRiskMemo}
                              className="h-7 px-2 text-[11px] font-semibold rounded text-white flex items-center gap-1"
                              style={{ background: "#B91C1C" }}
                              data-testid="compare-memo-btn">
                        <FileDown className="w-3 h-3" /> Export High-Risk Memo
                      </button>
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500 mb-3">
                    {c.ai_compares[c.ai_compares.length - 1].original_filename} vs {c.ai_compares[c.ai_compares.length - 1].revised_filename}
                    {" · "}Generated {timeAgo(c.ai_compares[c.ai_compares.length - 1].generated_at)}
                  </div>
                  {renderMd(filterCompareContent(c.ai_compares[c.ai_compares.length - 1].content_md, compareHighOnly))}
                </div>
              )}
            </div>
          )}

          {tab === "approvals" && (
            <ApprovalsPanel contractId={id} contract={c} currentUser={user} onChange={reload} />
          )}

          {tab === "redflags" && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Red Flags — {c.red_flags?.length || 0}</div>
                {canFlags && (
                  <button onClick={addFlag} className="h-8 px-3 text-xs font-semibold border border-slate-200 rounded flex items-center gap-1.5" data-testid="add-flag-btn">
                    <Plus className="w-3.5 h-3.5" /> Add Red Flag
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {(c.red_flags || []).map(f => (
                  <div key={f.id} className={`bg-white border-l-4 border border-slate-200 rounded-md p-3 ${f.severity === "High" ? "border-l-red-500" : f.severity === "Medium" ? "border-l-amber-500" : "border-l-slate-400"}`} data-testid={`flag-${f.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-slate-900">{RED_FLAG_TYPES.find(t => t.key === f.flag_type)?.label || f.flag_type}</span>
                          <span className={`chip ${f.severity === "High" ? "chip-overdue" : f.severity === "Medium" ? "chip-duesoon" : "chip-draft"}`}><span className="dot" />{f.severity}</span>
                          {f.resolved && <span className="chip chip-ontrack"><span className="dot" />Resolved</span>}
                          {f.accepted_by_gc && <span className="chip chip-completed"><span className="dot" />GC Accepted</span>}
                        </div>
                        {f.notes && <div className="text-xs text-slate-600 mt-1">{f.notes}</div>}
                        {f.clause_ref && <div className="text-[10px] text-slate-500 mt-1">Clause: {f.clause_ref}</div>}
                        {f.renewal_date && <div className="text-[10px] text-slate-500 mt-1">Renewal date: {new Date(f.renewal_date).toLocaleDateString()}</div>}
                      </div>
                      {canFlags && !f.resolved && (
                        <div className="flex gap-1">
                          <button onClick={() => updateFlag(f.id, { resolved: true })} className="text-xs px-2 py-1 hover:bg-slate-50 rounded" title="Resolve"><CheckCircle2 className="w-4 h-4 text-green-600" /></button>
                          {user.role === "general_counsel" && (
                            <button onClick={() => updateFlag(f.id, { accepted_by_gc: true })} className="text-xs px-2 py-1 hover:bg-slate-50 rounded" title="GC Accept" data-testid={`accept-${f.id}`}><Flag className="w-4 h-4 text-[#005289]" /></button>
                          )}
                        </div>
                      )}
                      {f.renewal_date && (user?.role === "department_director" || user?.role === "general_counsel" || user?.role === "it_admin" || user?.role === "compliance") && !(f.snoozed_milestones || []).includes(30) && (
                        <button
                          onClick={async () => {
                            if (!window.confirm("Snooze the 30-day renewal alert for this flag? You will not receive the email.")) return;
                            try {
                              await api.post(`/contracts/${id}/red-flags/${f.id}/snooze?milestone=30`);
                              toast.success("30-day alert snoozed");
                              reload();
                            } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                          }}
                          className="ml-2 text-[10px] px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold"
                          data-testid={`snooze-30-${f.id}`}>Snooze 30-day alert</button>
                      )}
                      {(f.snoozed_milestones || []).includes(30) && (
                        <span className="ml-2 chip chip-draft" data-testid={`snoozed-30-${f.id}`}><span className="dot" />30-day snoozed</span>
                      )}
                    </div>
                  </div>
                ))}
                {(!c.red_flags || c.red_flags.length === 0) && <div className="text-center py-8 text-sm text-slate-500">No red flags recorded.</div>}
              </div>
            </div>
          )}

          {tab === "checklist" && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Analyst Checklist</div>
              <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
                {CHECKLIST_ITEMS.map(item => {
                  const s = c.checklist?.[item.key] || { completed: false };
                  return (
                    <div key={item.key} className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        <input type="checkbox" checked={s.completed} onChange={e => setChecklist(item.key, e.target.checked)} disabled={!canLegal} data-testid={`check-${item.key}`} />
                        <div>
                          <div className="text-sm font-semibold text-slate-900">{item.label}</div>
                          <div className="text-xs text-slate-500">{s.completed ? "Completed" : "Pending"}</div>
                        </div>
                      </div>
                      {s.completed ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-slate-300" />}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab === "documents" && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Documents — {c.documents?.length || 0}</div>
                {canLegal && (
                  <label className="h-8 px-3 text-xs font-semibold border border-slate-200 rounded cursor-pointer flex items-center gap-1.5 hover:bg-slate-50" data-testid="upload-more-btn">
                    <Upload className="w-3.5 h-3.5" /> Upload
                    <input type="file" className="hidden" onChange={e => e.target.files[0] && uploadDoc("attachment", e.target.files[0])} />
                  </label>
                )}
              </div>
              <div className="bg-white border border-slate-200 rounded-md">
                {(c.documents || []).map(d => (
                  <div key={d.id} className="border-b border-slate-100 last:border-b-0" data-testid={`doc-${d.id}`}>
                    <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50">
                      <FileText className="w-4 h-4 text-slate-500 cursor-pointer" onClick={() => previewDoc(d.id)}/>
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => previewDoc(d.id)}>
                        <div className="text-sm font-semibold text-slate-900">{d.filename}</div>
                        <div className="text-[10px] text-slate-500">{d.kind} · {(d.size/1024).toFixed(1)} KB · uploaded by {d.uploaded_by}</div>
                      </div>
                      <span className="chip chip-draft"><span className="dot" />{d.kind}</span>
                      <ClassificationChip d={d} contractId={id} canEdit={canLegal} onChange={reload}/>
                    </div>
                  </div>
                ))}
                {(!c.documents || c.documents.length === 0) && <div className="text-center py-8 text-sm text-slate-500">No documents uploaded.</div>}
              </div>

              {pdfPreview && (
                <div className="mt-4 bg-white border border-slate-200 rounded-md" data-testid="pdf-viewer-panel">
                  <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
                    <div className="text-xs font-bold text-slate-900 flex items-center gap-2">
                      <FileText className="w-3.5 h-3.5 text-[#005289]" />
                      {pdfPreview.filename}
                    </div>
                    <button onClick={() => setPdfPreview(null)} className="text-slate-400 hover:text-slate-700 text-xs" data-testid="close-preview">
                      Close preview
                    </button>
                  </div>
                  <iframe title="pdf-preview" src={pdfPreview.url} className="pdf-viewer" />
                </div>
              )}

              {/* Compliance flags panel */}
              {(c.compliance_flags?.length || canCompliance) && (
                <div className="mt-4 bg-white border border-slate-200 rounded-md">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                      <Flag className="w-3.5 h-3.5 text-red-600" />
                      Compliance Flags ({c.compliance_flags?.length || 0})
                    </div>
                    {canCompliance && (
                      <button onClick={raiseComplianceFlag} className="text-xs font-semibold text-red-700 hover:underline" data-testid="raise-compliance-inline">+ Raise flag</button>
                    )}
                  </div>
                  <div className="divide-y divide-slate-100">
                    {(c.compliance_flags || []).map(f => (
                      <div key={f.id} className="px-4 py-3 flex items-start gap-3" data-testid={`comp-flag-${f.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-900">{f.note}</div>
                          <div className="text-[11px] text-slate-500 mt-0.5">Raised by {f.raised_by}</div>
                        </div>
                        {f.cleared
                          ? <span className="chip chip-ontrack"><span className="dot" />Cleared</span>
                          : (canClearCompliance ? (
                              <button onClick={() => clearComplianceFlag(f.id)}
                                      className="chip chip-overdue hover:opacity-80"
                                      data-testid={`clear-comp-${f.id}`}>
                                <span className="dot" />Clear
                              </button>
                            ) : <span className="chip chip-overdue"><span className="dot" />Open · blocks execution</span>)}
                      </div>
                    ))}
                    {(!c.compliance_flags || c.compliance_flags.length === 0) && (
                      <div className="px-4 py-4 text-xs text-slate-500">No compliance flags raised.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "comments" && (
            <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Threaded Comments</div>
                <div className="bg-white border border-slate-200 rounded-md">
                  <div className="max-h-80 overflow-y-auto custom-scroll">
                    {(c.comments || []).map(m => (
                      <div key={m.id} className="px-4 py-3 border-b border-slate-100">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-bold text-slate-900">{m.author_name}</span>
                          {m.internal && <span className="chip chip-draft"><span className="dot" />Internal</span>}
                          <span className="text-[10px] text-slate-500">{timeAgo(m.created_at)}</span>
                        </div>
                        <div className="text-sm text-slate-700">{m.body}</div>
                      </div>
                    ))}
                    {(!c.comments || c.comments.length === 0) && <div className="text-center py-6 text-xs text-slate-500">No comments yet.</div>}
                  </div>
                  <div className="border-t border-slate-100 p-2 flex gap-2 relative">
                    <input ref={commentRef} value={commentBody}
                          onChange={e => onCommentChange(e.target.value)}
                          onKeyDown={e => e.key === "Enter" && !mentionOpen && addComment()}
                          placeholder="Add an internal comment... type @ to mention"
                          className="flex-1 h-8 px-2 text-sm border border-slate-200 rounded" data-testid="comment-input" />
                    <button onClick={addComment} className="h-8 px-3 text-white rounded text-xs" style={{ background: "#005289" }} data-testid="comment-send"><Send className="w-3.5 h-3.5" /></button>
                    {mentionOpen && filteredMentions.length > 0 && (
                      <div className="absolute bottom-10 left-2 w-64 bg-white border border-slate-200 rounded-md shadow-lg z-10 max-h-56 overflow-y-auto" data-testid="mention-dropdown">
                        {filteredMentions.slice(0, 8).map(u => (
                          <div key={u.id} onClick={() => insertMention(u)}
                               className="px-3 py-2 text-xs hover:bg-slate-50 cursor-pointer flex items-center gap-2"
                               data-testid={`mention-${u.email}`}>
                            <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600 overflow-hidden">
                              {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-full h-full object-cover" /> : (u.name||"?").slice(0,1)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-slate-900 truncate">{u.name}</div>
                              <div className="text-[10px] text-slate-500 truncate">{u.title}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {pendingMentions.length > 0 && (
                    <div className="px-2 pb-2 flex flex-wrap gap-1">
                      {pendingMentions.map(m => (
                        <span key={m.id} className="chip chip-completed"><span className="dot" />@{m.name}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Activity Feed</div>
                <div className="bg-white border border-slate-200 rounded-md max-h-96 overflow-y-auto custom-scroll">
                  {(c.activity || []).slice().reverse().map((a, i) => (
                    <div key={i} className="px-4 py-2.5 border-b border-slate-100 last:border-b-0 text-xs">
                      <div><span className="font-bold text-slate-900">{a.actor}</span> <span className="text-slate-500">{a.action}</span></div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{new Date(a.at).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "notes" && (
            <div className="space-y-4" data-testid="tab-notes-body">
              {/* Full correspondence log */}
              <div className="bg-white border border-slate-200 rounded-md" data-testid="correspondence-panel">
                <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Correspondence Log</div>
                  <span className="text-[10px] text-slate-500">{correspondence.length} record{correspondence.length===1?"":"s"} · vendor invites, chats, notes & external emails</span>
                </div>
                <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                  {correspondence.length === 0 && <div className="p-4 text-xs text-slate-500 text-center">No correspondence recorded yet.</div>}
                  {correspondence.map((e, i) => (
                    <div key={i} className="px-4 py-3 text-xs" data-testid={`corr-${e.type}-${i}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`chip ${e.type === "vendor_link" ? "chip-completed" : e.type === "vendor_chat" ? "chip-ontrack" : e.type === "external_email" ? "chip-duesoon" : "chip-draft"}`}>
                          <span className="dot" />{e.type.replace(/_/g, " ")}
                        </span>
                        <span className="text-[10px] text-slate-500">{new Date(e.at || e.sent_at).toLocaleString()}</span>
                        {e.sent_by_name && <span className="text-[10px] font-semibold ml-1">{e.sent_by_name}</span>}
                        {e.author_name && <span className="text-[10px] font-semibold ml-1">{e.author_name}</span>}
                        {e.from && <span className="text-[10px] font-semibold ml-1">from {e.from}</span>}
                      </div>
                      {e.type === "vendor_link" && (
                        <div className="text-slate-700">
                          <div>To <b>{e.vendor_name}</b> &lt;{e.vendor_email || "—"}&gt;{e.cc_emails?.length ? ` · CC: ${e.cc_emails.join(", ")}` : ""}</div>
                          {e.message && <div className="text-slate-600 mt-1 whitespace-pre-wrap">{e.message}</div>}
                          <div className="text-[10px] text-slate-500 mt-1">Expires {e.expires_at ? e.expires_at.slice(0,10) : "—"}</div>
                        </div>
                      )}
                      {(e.type === "vendor_chat" || e.type === "note" || e.type === "external_email") && (
                        <div className="text-slate-700 whitespace-pre-wrap">{e.body}</div>
                      )}
                      {(e.attachments || []).map((a, k) => (
                        <div key={k} className="mt-1 text-[10px] text-slate-500 flex items-center gap-1"><FileText className="w-3 h-3" />{a.filename}</div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {/* Notes column */}
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Contract Notes ({notes.length})</div>
                  <div className="bg-white border border-slate-200 rounded-md">
                    <div className="p-3 border-b border-slate-100 space-y-2">
                      <MentionInput value={newNote} onChange={setNewNote}
                                    team={team} onMentionsChange={setNoteMentions}
                                    resetSignal={noteResetTick}
                                    placeholder="Add a note (e.g., call summary, negotiation point)… Type @ to tag a teammate."
                                    testIdPrefix="note-body" rows={3} minHeight="70px" />
                      <div className="flex items-center gap-2 flex-wrap">
                        <label className="inline-flex items-center gap-1 text-[11px] text-slate-600 cursor-pointer border border-slate-200 rounded px-2 py-1 hover:bg-slate-50">
                          <Upload className="w-3 h-3" /> {newNoteFile ? newNoteFile.name.slice(0, 20) : "Attach file"}
                          <input type="file" className="hidden" onChange={e => setNewNoteFile(e.target.files[0])} data-testid="note-file" />
                        </label>
                        {(user?.role === "compliance" || ["contract_analyst","attorney","legal_manager","general_counsel"].includes(user?.role)) && (
                          <select value={newNoteVisibility} onChange={e => setNewNoteVisibility(e.target.value)}
                                  data-testid="note-visibility"
                                  className="h-7 px-2 text-[11px] border border-slate-200 rounded bg-white text-slate-700">
                            <option value="">Default visibility</option>
                            {["contract_analyst","attorney","legal_manager","general_counsel"].includes(user?.role) && (
                              <option value="internal">Internal (Legal only)</option>
                            )}
                            <option value="compliance">Compliance topic (Legal + Compliance)</option>
                            <option value="shared">Shared (everyone with access)</option>
                          </select>
                        )}
                        <label className="inline-flex items-center gap-1 text-[11px] text-slate-600">
                          <input type="checkbox" checked={newNoteExternal} onChange={e => setNewNoteExternal(e.target.checked)} data-testid="note-external" /> Share with vendor
                        </label>
                        <button onClick={addNote} className="ml-auto h-7 px-3 text-white rounded text-xs font-semibold"
                                style={{ background: "#005289" }} data-testid="note-add">Save Note</button>
                      </div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notes.length === 0 && <div className="p-4 text-xs text-slate-500 text-center">No notes yet.</div>}
                      {notes.slice().reverse().map(n => (
                        <div key={n.id} className="px-4 py-3 border-b border-slate-100" data-testid={`note-${n.id}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-bold">{n.author_name}</span>
                            <span className="text-[10px] text-slate-500">{n.author_role}</span>
                            {n.visibility === "compliance" && <span className="chip chip-duesoon" data-testid={`note-vis-compliance-${n.id}`}><span className="dot" />Compliance Topic</span>}
                            {n.visibility === "internal" && <span className="chip chip-draft"><span className="dot" />Internal Legal Only</span>}
                            {n.visibility === "shared" && <span className="chip chip-completed"><span className="dot" />Shared</span>}
                            {!n.visibility && n.external_visible && <span className="chip chip-completed"><span className="dot" />Visible to Requester</span>}
                            {!n.visibility && !n.external_visible && <span className="chip chip-draft"><span className="dot" />Internal Legal Only</span>}
                            <span className="ml-auto text-[10px] text-slate-400">{new Date(n.created_at).toLocaleString()}</span>
                          </div>
                          {n.body && <div className="text-sm text-slate-700 whitespace-pre-wrap">{n.body}</div>}
                          {(n.attachments || []).map((a, i) => (
                            <button key={i} onClick={async () => {
                              try {
                                const r = await api.get(`/contracts/${id}/notes/${n.id}/attachments/${i}`, { responseType: "blob" });
                                const url = URL.createObjectURL(r.data);
                                const link = document.createElement("a");
                                link.href = url; link.download = a.filename;
                                document.body.appendChild(link); link.click(); link.remove();
                                URL.revokeObjectURL(url);
                              } catch { toast.error("Download failed"); }
                            }}
                            className="mt-2 flex items-center gap-2 text-xs text-slate-600 bg-slate-50 hover:bg-slate-100 px-2 py-1.5 rounded w-full text-left"
                            data-testid={`note-attach-${n.id}-${i}`}>
                              <FileText className="w-3.5 h-3.5" />
                              <span className="font-semibold text-[#005289]">{a.filename}</span>
                              <span className="text-slate-400 ml-auto">{(a.size/1024).toFixed(1)} KB</span>
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Vendor messages column */}
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2">
                    Vendor Portal Chat ({vendorMessages.length})
                    {unreadVendorMsgs > 0 && (
                      <span className="chip chip-overdue" data-testid="vendor-unread-badge">
                        <span className="dot" />{unreadVendorMsgs} new
                      </span>
                    )}
                    {unreadVendorMsgs > 0 && (
                      <button onClick={markVendorRead} className="ml-auto text-[10px] text-[#005289] hover:underline" data-testid="mark-vendor-read">Mark read</button>
                    )}
                  </div>
                  <div className="bg-white border border-slate-200 rounded-md">
                    <div className="p-3 max-h-72 overflow-y-auto space-y-2">
                      {vendorMessages.length === 0 && <div className="text-xs text-slate-500 text-center py-3">No messages with vendor yet.</div>}
                      {vendorMessages.map(m => (
                        <div key={m.id} className={`flex ${m.from === "hcd" ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[85%] rounded-lg px-3 py-1.5 ${m.from === "hcd" ? "bg-[#005289] text-white" : "bg-slate-100 text-slate-900"}`}>
                            <div className="text-[10px] font-bold opacity-80">{m.author_name}</div>
                            <div className="text-sm">{m.body}</div>
                            <div className="text-[10px] opacity-70 mt-0.5">{new Date(m.at).toLocaleString()}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {canLegal && (
                      <div className="p-2 border-t border-slate-100 flex gap-2">
                        <input value={vendorReplyBody} onChange={e => setVendorReplyBody(e.target.value)}
                              onKeyDown={e => e.key === "Enter" && sendVendorMsg()}
                              placeholder="Reply to vendor..."
                              className="flex-1 h-8 px-2 text-sm border border-slate-200 rounded" data-testid="vendor-reply-input" />
                        <button onClick={sendVendorMsg} className="h-8 px-3 text-white rounded text-xs" style={{ background: "#005289" }} data-testid="vendor-reply-send">
                          <Send className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Signature requests */}
              {canLegal && (
                <div className="bg-white border border-slate-200 rounded-md" data-testid="signature-panel">
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Signature Requests</div>
                    <span className="ml-auto text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 font-bold">
                      MOCKED E-SIG
                    </span>
                  </div>
                  <div className="p-3 grid grid-cols-3 gap-2">
                    <input value={sigName} onChange={e => setSigName(e.target.value)} placeholder="Signer name" className="h-8 px-2 text-xs border border-slate-200 rounded" data-testid="sig-name" />
                    <input value={sigEmail} onChange={e => setSigEmail(e.target.value)} placeholder="Signer email" className="h-8 px-2 text-xs border border-slate-200 rounded" data-testid="sig-email" />
                    <button onClick={requestSignature} className="h-8 px-3 text-white rounded text-xs font-semibold" style={{ background: "#00AE42" }} data-testid="send-signature-req">Send for Signature</button>
                  </div>
                  <div className="px-4 pb-3 divide-y divide-slate-100">
                    {(c.signature_requests || []).map(s => (
                      <div key={s.id} className="py-2 flex items-center gap-2 text-xs">
                        <div className="flex-1">
                          <div className="font-semibold">{s.signer_name} <span className="text-slate-500 font-normal">· {s.signer_email}</span></div>
                          <div className="text-[10px] text-slate-500">{s.signer_role} · sent {new Date(s.created_at).toLocaleDateString()}</div>
                        </div>
                        {s.status === "signed"
                          ? <span className="chip chip-ontrack"><span className="dot" />Signed {new Date(s.signed_at).toLocaleDateString()}</span>
                          : <span className="chip chip-duesoon"><span className="dot" />Pending</span>}
                      </div>
                    ))}
                    {!(c.signature_requests || []).length && <div className="py-3 text-xs text-slate-500 text-center">No signature requests yet.</div>}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "timeline" && (
            <div className="space-y-4" data-testid="tab-timeline-body">
              {c.parent_contract_id && <AmendmentDiff contractId={id} />}
              <div className="bg-white border border-slate-200 rounded-md">
                <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-bold text-slate-900 flex items-center gap-2">
                      <Activity className="w-4 h-4 text-[#005289]" /> Contract Timeline
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {timeline?.count || 0} events · every intake, approval, movement, message, upload & external interaction
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  {!timeline && <div className="text-xs text-slate-500 text-center py-8">Loading timeline...</div>}
                  {timeline && timeline.events.length === 0 && <div className="text-xs text-slate-500 text-center py-8">No events yet.</div>}
                  {timeline && timeline.events.length > 0 && (
                    <ol className="relative border-l-2 border-slate-200 ml-3" data-testid="timeline-list">
                      {timeline.events.map((e, i) => {
                        const catColor = {
                          activity: "#005289", stage: "#00AE42", document: "#7C3AED",
                          red_flag: "#EF4444", comment: "#0EA5E9", note: "#F59E0B",
                          vendor_chat: "#DB2777", correspondence: "#0891B2",
                          external: "#B91C1C", amendment: "#7C3AED",
                          signature: "#059669", checklist: "#65A30D", audit: "#334155",
                        }[e.category] || "#64748B";
                        return (
                          <li key={i} className="mb-4 ml-6" data-testid={`timeline-${e.category}-${i}`}>
                            <span className="absolute -left-[9px] w-4 h-4 rounded-full border-2 border-white shadow" style={{ background: catColor }} />
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="chip chip-draft text-[10px]"><span className="dot" style={{ background: catColor }} />{e.category.replace(/_/g, " ")}</span>
                              <span className="text-xs font-bold text-slate-900">{e.actor || "—"}</span>
                              <span className="text-[10px] text-slate-500">{new Date(e.at).toLocaleString()}</span>
                              {e.ip && <span className="text-[10px] text-red-700 font-mono bg-red-50 px-1.5 py-0.5 rounded">IP {e.ip}</span>}
                            </div>
                            <div className="mt-0.5 text-sm text-slate-800">{e.title}</div>
                            {e.detail && <div className="mt-0.5 text-[12px] text-slate-600 whitespace-pre-wrap">{e.detail}</div>}
                            {e.user_agent && <div className="mt-0.5 text-[10px] text-slate-400 truncate" title={e.user_agent}>UA: {e.user_agent}</div>}
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </div>
              </div>
            </div>
          )}

          {tab === "timers" && (
            <div className="space-y-6" data-testid="tab-timers-body">
              <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Timers & Reminders</div>
                <div className="bg-white border border-slate-200 rounded-md">
                  <div className="p-3 border-b border-slate-100 flex gap-2">
                    <input value={newTimerLabel} onChange={e => setNewTimerLabel(e.target.value)}
                           placeholder="e.g., follow up with vendor" className="flex-1 h-8 px-2 text-sm border border-slate-200 rounded"
                           data-testid="timer-label" />
                    <input type="date" value={newTimerDate} onChange={e => setNewTimerDate(e.target.value)}
                           className="h-8 px-2 text-sm border border-slate-200 rounded" data-testid="timer-date" />
                    <button onClick={addTimer} className="h-8 px-3 text-white text-xs font-semibold rounded"
                            style={{ background: "#005289" }} data-testid="timer-add">Add</button>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {timers.length === 0 && <div className="p-4 text-xs text-slate-500 text-center">No reminders set.</div>}
                    {timers.map(t => {
                      const days = Math.round((new Date(t.due_date) - new Date()) / 86400000);
                      return (
                        <div key={t.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`timer-${t.id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-slate-900">{t.label}</div>
                            <div className="text-[11px] text-slate-500">{new Date(t.due_date).toLocaleDateString()}</div>
                          </div>
                          <span className={`chip ${days < 0 ? "chip-overdue" : days <= 3 ? "chip-duesoon" : "chip-ontrack"}`}>
                            <span className="dot" />{days < 0 ? `${Math.abs(days)}d late` : `${days}d`}
                          </span>
                          <button onClick={() => removeTimer(t.id)} className="text-slate-400 hover:text-red-600 text-xs">Remove</button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center justify-between">
                  Amendments ({amendments.length})
                  {canLegal && (
                    <button onClick={createAmendment} className="text-xs font-semibold text-[#005289] hover:underline" data-testid="new-amendment">+ New Amendment</button>
                  )}
                </div>
                <div className="bg-white border border-slate-200 rounded-md">
                  {amendments.length === 0 && (
                    <div className="p-4 text-xs text-slate-500 text-center">
                      No amendments. Amendments will be numbered {c.contract_number}.1, .2, .3…
                    </div>
                  )}
                  {amendments.map(a => (
                    <div key={a.id} onClick={() => nav(`/contract/${a.id}`)}
                         className="px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer"
                         data-testid={`amendment-${a.contract_number}`}>
                      <div className="flex items-center justify-between">
                        <div className="font-mono text-xs text-[#005289] font-bold">{a.contract_number}</div>
                        <span className="chip chip-draft"><span className="dot" />{(a.stage || "").replace(/_/g, " ")}</span>
                      </div>
                      <div className="text-sm font-semibold text-slate-900 mt-1">{a.description}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">Amount: ${(a.contract_amount || 0).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
              </div>
            </div>
          )}

          {tab === "original" && <OriginalSubmissionTab contractId={id} />}
          {tab === "evaluation" && <EvaluationTab contractId={id} contract={c} onChanged={reload} />}
          {tab === "milestones" && <MilestonesTab contractId={id} />}
          {tab === "spend" && <SpendPanel contract={c} />}

          {/* Execute & File modal — required upload before moving stage to Executed */}
          {executeModal ? (
            <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
                 onClick={() => !execBusy && setExecuteModal(false)} data-testid="execute-modal">
              <div className="bg-white rounded-md w-[520px] max-w-[95vw]" onClick={e => e.stopPropagation()}>
                <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
                  <FileDown className="w-4 h-4 text-[#00AE42]" />
                  <div className="font-bold text-sm">Execute & File in HCD Contract Central</div>
                  <button onClick={() => !execBusy && setExecuteModal(false)}
                          className="ml-auto text-slate-400 hover:text-slate-800"
                          data-testid="execute-modal-close">✕</button>
                </div>
                <div className="p-5 space-y-3 text-xs">
                  <div className="bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
                    Attach the <b>fully-executed</b> contract file (all signatures). The system will
                    file it in HCD Contract Central under <b>{c?.department || "the department folder"}</b>.
                    Contracts cannot move to <b>Executed</b> until this is uploaded.
                  </div>

                  {(c?.documents || []).some(d => ["executed","signed_final","fully_executed"].includes((d.kind || "").toLowerCase())) ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-800" data-testid="execute-modal-doc-exists">
                      A fully-executed file is already on this contract. You can execute directly, or upload a replacement below.
                    </div>
                  ) : null}

                  <label className="block">
                    <span className="font-bold text-slate-700 block mb-1">Fully-executed contract (PDF)</span>
                    <input type="file" accept=".pdf,.docx" data-testid="execute-modal-file"
                           onChange={e => setExecFile(e.target.files?.[0] || null)}
                           className="text-xs w-full" />
                    <div className="text-[11px] text-slate-400 mt-1">{execFile ? `Selected: ${execFile.name}` : "No file chosen"}</div>
                  </label>

                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="font-bold text-slate-700 block mb-1">Effective date</span>
                      <input type="date" value={execEffective} onChange={e => setExecEffective(e.target.value)}
                             data-testid="execute-modal-effective" className="w-full h-8 px-2 border border-slate-200 rounded" />
                    </label>
                    <label className="block">
                      <span className="font-bold text-slate-700 block mb-1">End / expiration date</span>
                      <input type="date" value={execEnd} onChange={e => setExecEnd(e.target.value)}
                             data-testid="execute-modal-end" className="w-full h-8 px-2 border border-slate-200 rounded" />
                    </label>
                  </div>

                  {sigResult ? (
                    <div className={`border rounded p-2 ${sigResult.all_signatures_present ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"}`} data-testid="execute-modal-sig-result">
                      <div className="font-bold mb-1">
                        {sigResult.all_signatures_present ? "✓ All required signatures detected" : "⚠ Signature check flagged issues"}
                      </div>
                      {sigResult.error ? <div>{sigResult.error}</div> : null}
                      {(sigResult.signatures_found || []).length ? (
                        <div className="mt-1"><b>Found:</b> {sigResult.signatures_found.map((s, i) =>
                          <span key={i}> {s.party}{s.signatory_name ? ` — ${s.signatory_name}` : ""}{i < sigResult.signatures_found.length - 1 ? " ·" : ""}</span>
                        )}</div>
                      ) : null}
                      {(sigResult.signatures_missing || []).length ? (
                        <div className="mt-1"><b>Missing:</b> {sigResult.signatures_missing.join(", ")}</div>
                      ) : null}
                      {sigResult.notes ? <div className="mt-1 italic">{sigResult.notes}</div> : null}
                      {!sigResult.all_signatures_present ? (
                        <label className="flex items-center gap-2 mt-2 text-red-900">
                          <input type="checkbox" checked={overrideMissing} onChange={e => setOverrideMissing(e.target.checked)}
                                 data-testid="execute-modal-override" />
                          <span className="text-[11px] font-semibold">Execute anyway (overrides signature check — audited)</span>
                        </label>
                      ) : null}
                    </div>
                  ) : null}

                  {execStep ? (
                    <div className="text-[11px] text-slate-500" data-testid="execute-modal-step">
                      {execStep === "uploading" ? "Uploading executed file…" :
                       execStep === "verifying" ? "AI verifying signatures…" :
                       execStep === "executing" ? "Filing in HCD Contract Central…" : ""}
                    </div>
                  ) : null}
                </div>
                <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
                  <button onClick={() => setExecuteModal(false)} disabled={execBusy}
                          className="h-9 px-3 text-xs border border-slate-200 rounded">Cancel</button>
                  <button onClick={executeAndFile} disabled={execBusy} data-testid="execute-modal-submit"
                          className="h-9 px-4 rounded text-xs font-bold text-white disabled:opacity-60"
                          style={{ background: "#00AE42" }}>
                    {execBusy ? "Executing…" : "Upload & Execute"}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {tab === "details" && (
            <div className="grid grid-cols-2 gap-6">
              <div className="col-span-2 bg-white border border-slate-200 rounded-md p-4" data-testid="details-submitted-by">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Submitted By</div>
                <dl className="text-sm grid grid-cols-2 gap-x-6 gap-y-1.5">
                  <div className="flex justify-between"><dt className="text-slate-500">Sender name</dt><dd className="font-semibold">{c.submitted_by_name || "—"}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Login email</dt><dd className="font-mono text-xs">{c.submitted_by_email || "—"}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Role</dt><dd>{c.submitted_by_role ? c.submitted_by_role.replace(/_/g, " ") : "—"}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Submitted at</dt><dd className="text-xs">{c.initial_received_date ? new Date(c.initial_received_date).toLocaleString() : "—"}</dd></div>
                  {c.on_behalf_of_name && (
                    <div className="flex justify-between col-span-2 bg-amber-50 border border-amber-200 rounded p-2">
                      <dt className="text-amber-800 font-semibold">Submitted on behalf of</dt>
                      <dd className="font-semibold text-amber-900">{c.on_behalf_of_name} <span className="font-mono text-xs">({c.on_behalf_of_email})</span></dd>
                    </div>
                  )}
                </dl>
              </div>
              <div className="bg-white border border-slate-200 rounded-md p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Vendor</div>
                <dl className="text-sm space-y-1.5">
                  <div className="flex justify-between"><dt className="text-slate-500">Legal Name</dt><dd className="font-semibold">{c.vendor_legal_name}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">DBA</dt><dd>{c.vendor_dba || "—"}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Contact</dt><dd>{c.vendor_contact_name}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Email</dt><dd>{c.vendor_contact_email}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Phone</dt><dd>{c.vendor_contact_phone || "—"}</dd></div>
                </dl>
              </div>
              <div className="bg-white border border-slate-200 rounded-md p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Financial</div>
                <dl className="text-sm space-y-1.5">
                  <div className="flex justify-between"><dt className="text-slate-500">Contract Amount</dt><dd>${(c.contract_amount || 0).toLocaleString()}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">One-Time Cost</dt><dd>${(c.one_time_cost || 0).toLocaleString()}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Capital Cost</dt><dd>${(c.capital_cost || 0).toLocaleString()}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">PO #</dt><dd className="font-mono text-xs">{c.po_number || "—"}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Budgeted</dt><dd>{c.is_budgeted}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Finance Committee</dt><dd>{c.financial_grid?.finance_committee_approval?.answer || "—"}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Board Approval</dt><dd>{c.financial_grid?.board_approval?.answer || "—"}</dd></div>
                </dl>
              </div>
              <div className="col-span-2 bg-white border border-slate-200 rounded-md p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Purpose</div>
                <div className="text-sm text-slate-700 whitespace-pre-line">{c.purpose}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Priority modal */}
      {priorityOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="priority-modal">
          <div className="bg-white rounded-md max-w-lg w-full shadow-xl">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-sm font-bold text-slate-900">Mark Contract as PRIORITY</div>
                <div className="text-[11px] text-slate-500">#{c.contract_number} · {c.vendor_legal_name}</div>
              </div>
              <button onClick={() => setPriorityOpen(false)} className="text-slate-400 hover:text-slate-700" data-testid="priority-close"><XCircle className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1.5">Reason <span className="text-red-500">*</span></label>
                <textarea value={priorityReason} onChange={e => setPriorityReason(e.target.value)}
                          placeholder="Why is this contract a priority? (e.g., Board deadline, patient safety, regulatory)"
                          className="w-full min-h-[90px] p-2 text-sm border border-slate-200 rounded" data-testid="priority-reason" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1.5">Needed by (optional)</label>
                <input type="date" value={priorityDate} onChange={e => setPriorityDate(e.target.value)}
                       className="h-9 px-3 text-sm border border-slate-200 rounded w-full" data-testid="priority-date" />
                <div className="text-[11px] text-slate-500 mt-1">Team lead, assigned attorney/analyst, and Department Director will be notified.</div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setPriorityOpen(false)} className="h-8 px-3 text-xs font-semibold border border-slate-200 rounded">Cancel</button>
              <button onClick={savePriority} className="h-8 px-3 text-white rounded text-xs font-semibold" style={{ background: "#B91C1C" }} data-testid="priority-save">
                Flag as PRIORITY
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vendor Link modal */}
      {vendorLinkOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="vendor-link-modal">
          <div className="bg-white rounded-md max-w-lg w-full shadow-xl">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-sm font-bold text-slate-900">Send Vendor Portal Link</div>
                <div className="text-[11px] text-slate-500">#{c.contract_number} · Invite vendor and copy others into the chat</div>
              </div>
              <button onClick={() => setVendorLinkOpen(false)} className="text-slate-400 hover:text-slate-700" data-testid="vlink-close"><XCircle className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1.5">Vendor Contact Name</label>
                <input value={vlName} onChange={e => setVlName(e.target.value)}
                       className="w-full h-9 px-3 text-sm border border-slate-200 rounded" data-testid="vlink-name" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1.5">Vendor Email <span className="text-red-500">*</span></label>
                <input type="email" value={vlEmail} onChange={e => setVlEmail(e.target.value)}
                       className="w-full h-9 px-3 text-sm border border-slate-200 rounded" data-testid="vlink-email" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1.5">CC Other Recipients</label>
                <input value={vlCc} onChange={e => setVlCc(e.target.value)}
                       placeholder="alice@vendor.com, bob@hcdpbc.org"
                       className="w-full h-9 px-3 text-sm border border-slate-200 rounded" data-testid="vlink-cc" />
                <div className="text-[11px] text-slate-500 mt-1">Comma- or semicolon-separated. Each recipient receives the portal invite and can join the discussion.</div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1.5">Message (optional)</label>
                <textarea value={vlMsg} onChange={e => setVlMsg(e.target.value)}
                          placeholder="Add a short note that will appear in the email…"
                          className="w-full min-h-[80px] p-2 text-sm border border-slate-200 rounded" data-testid="vlink-msg" />
              </div>
              <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
                A permanent record of this invite (recipients, message, delivery status) is filed under the contract's Correspondence log.
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setVendorLinkOpen(false)} className="h-8 px-3 text-xs font-semibold border border-slate-200 rounded">Cancel</button>
              <button onClick={sendVendorLink} className="h-8 px-3 text-white rounded text-xs font-semibold flex items-center gap-1.5" style={{ background: "#00AE42" }} data-testid="vlink-send">
                <Send className="w-3.5 h-3.5" /> Send Vendor Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
